/*
 * Why a form submit can appear to do nothing at all.
 *
 * react-hook-form's `handleSubmit` takes a second callback for the invalid case. Passing only
 * the first one means a failed validation returns in silence: no request, no toast, no console
 * entry, and a button that looks broken. That is survivable while every invalid field is on
 * screen — `FormField` prints the message underneath it — and invisible the moment a required
 * field is conditionally rendered, which is exactly how the appointment form ended up with a
 * dead Confirm button.
 *
 * Passing `reportFormErrors` as that second callback means a blocked submit always leaves a
 * trace: a line in the console for whoever is debugging, and a toast for whoever is clicking.
 */
import toast from "react-hot-toast";

const IGNORED_ERROR_KEYS = new Set(["ref", "type", "types", "message"]);

/**
 * react-hook-form nests errors by field path, and each leaf carries `{ type, message, ref }`
 * where `ref` is a DOM node — so recursion has to stop at anything holding a message and must
 * never walk into the node itself.
 */
export const collectFormErrors = (errors, pathPrefix = "") =>
  Object.entries(errors ?? {}).flatMap(([field, error]) => {
    if (!error || IGNORED_ERROR_KEYS.has(field)) return [];

    const path = pathPrefix ? `${pathPrefix}.${field}` : field;

    if (typeof error.message === "string" && !!error.message) {
      return [{ path, message: error.message }];
    }

    if (typeof error !== "object") return [];

    return collectFormErrors(error, path);
  });

/** "telephoneNumber" → "Telephone number", for naming a field the user never saw. */
export const humaniseFieldName = (path) => {
  const lastSegment = path.split(".").at(-1) ?? path;
  const spaced = lastSegment.replace(/([A-Z])/g, " $1").trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};

const buildMessage = (fieldErrors) => {
  const [first, ...rest] = fieldErrors;

  if (!first) return "Please check the form and try again.";

  if (rest.length === 0) return first.message;

  const otherFields = rest.map((error) => humaniseFieldName(error.path)).join(", ");

  return `${first.message} Also check: ${otherFields}.`;
};

/**
 * Writes the blocked submit to the console. `formName` is what identifies it, since a form that
 * refuses says nothing about which of several open forms it was.
 */
export const logFormErrors = ({ formName, errors }) => {
  const fieldErrors = collectFormErrors(errors);

  console.error(
    `[${formName}] submit blocked by validation:`,
    fieldErrors.length
      ? fieldErrors.map(({ path, message }) => `${path}: ${message}`)
      : errors
  );

  return fieldErrors;
};

/**
 * The console line plus a toast. For a form that can refuse on a field the user cannot see, where
 * the inline message under the field is not enough because there is no field on screen to put it
 * under.
 */
export const reportFormErrors = ({ formName, errors }) => {
  const fieldErrors = logFormErrors({ formName, errors });

  scrollToFirstError(fieldErrors);
  toast.error(buildMessage(fieldErrors));
};

/**
 * Brings the first refusing field into view.
 *
 * react-hook-form focuses the first invalid field itself, but only for fields registered with a
 * ref. Most fields in this codebase are Select components driven by `setValue`, which hold no
 * ref — so on a long form the message can render entirely off screen, above the button that was
 * just clicked, and the submit looks like it did nothing.
 */
export const scrollToFirstError = (fieldErrors) => {
  const [first] = fieldErrors;

  if (!first || typeof document === "undefined") return;

  const field = document.querySelector(`[data-form-field="${first.path}"]`);

  field?.scrollIntoView?.({ behavior: "smooth", block: "center" });
};

/**
 * Ready to pass as the second argument to `handleSubmit`:
 *
 *     onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("AddPatientForm"))}
 *
 * Console only. A form whose fields are all on screen already shows its messages underneath
 * them — this is about the submit leaving a trace for whoever is debugging it.
 */
export const onInvalidSubmit = (formName) => (errors) =>
  scrollToFirstError(logFormErrors({ formName, errors }));
